import { useQuery } from "@tanstack/react-query";
import { homepage } from "../../services/user";
import Loader from "../../components/loader/loader";
import dayjs from "dayjs";
import i18n from "../../i18n";
import { toast } from "react-toastify";
import { Card, Grid, Text } from "@mantine/core";
import AttentionTable from "./components/AttentionTable";
import UpForTodayChart from "./components/UpForTodayChart";
import LatestRuleChangesTable from "./components/LatestRuleChangesTable";
import LastActivityTable from "./components/LastActivityTable";
import classes from "./home.module.scss";
import PageWrapper from "../../components/Page/PageWrapper";
import { hourFormat } from "../../utils/dateFormat";

export default function Home() {
  const { t } = i18n;

  const {
    data: homepageData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["homepage-data"],
    queryFn: homepage,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  if (error) {
    toast.error(error.response?.data?.message);
    return null; // will be handled by TanStack Query error boundary if exists
  }


  if (isLoading) {
    return <Loader height={"80vh"} />;
  }

  const {
    user,
    completedCasesCount,
    awaitingCompletion,
    toBeMonitored,
    latestRuleChanges,
    latestActivities,
    awaitingCompletionPendingCount,
  } = homepageData;

  return (
    <PageWrapper>
      <div className={classes["homepage-wrapper"]}>
        <Text className={classes["center"]} fz={"lg"}>
          {t("homePage.hello", {
            name: user.name,
            date: dayjs(homepageData?.currentDate).format("DD-MM-YYYY"),
            hour: dayjs(homepageData?.currentDate).format(hourFormat),
          })}
        </Text>

        <Text className={classes["center"] + " " + classes["header-section"]} fz={"md"}>
          {t("homePage.subtitle", { cases: completedCasesCount })}
        </Text>

        <Grid gutter="lg" mt={40}>
          <Grid.Col span={4}>
            <Attention />
          </Grid.Col>

          <Grid.Col span={8}>
            <LastRuleChanges />
          </Grid.Col>

          <Grid.Col span={4}>
            <UpForToday />
          </Grid.Col>

          <Grid.Col span={8}>
            <LastActivity />
          </Grid.Col>
        </Grid>
      </div>
    </PageWrapper>

  );

  function Attention() {
    return (
      <>
        <Text fw="bold" c="red.4" pl={5} fz={"h4"}>
          {t("homePage.attention")}
        </Text>
        <Card
          withBorder
          style={(theme) => ({
            backgroundColor: theme.colors.gray[1],
            borderColor: theme.colors.red[3],
            paddingBottom: 0,
            borderWidth: 2,
            padding: 0,
          })}
        >
          <Text pl={5} pr={40} pt={10} pb={10} size="sm">
            {t("homePage.attentionMessage")}
          </Text>
          <AttentionTable items={toBeMonitored} />
        </Card>
      </>
    );
  }

  function LastRuleChanges() {
    return (
      <>
        <Text fw="bold" pl={5} fz={"h4"}>
          {t("homePage.ruleChanges")}
        </Text>
        <LatestRuleChangesTable items={latestRuleChanges} />
      </>
    );
  }

  function UpForToday() {
    const assigned = awaitingCompletion?.length || 0;
    const pending = awaitingCompletionPendingCount || 0;

    const chartData = [
      { name: "Assigned", value: assigned, color: "#0AD172" },
      { name: "Pending", value: pending, color: "#D9D9D9" },
    ];

    return (
      <>
        <Text fw="bold" pl={5} fz={"h4"}>
          {t("homePage.upForToday")}
        </Text>
        <UpForTodayChart
          assigned={assigned}
          pending={pending}
          chartData={chartData}
          user={user}
          t={t}
        />
      </>
    );
  }

  function LastActivity() {
    return (
      <>
        <Text fw="bold" pl={5} fz={"h4"}>
          {t("homePage.activity")}
        </Text>
        <LastActivityTable items={latestActivities} />
      </>
    );
  }
}
